// Copyright ©️ 2026 eVolve MEP, LLC

import type { FocusEvent } from 'react';

import type { BendInputKey, BendTypeInput } from '@evolvemep/api-ts';
import { useMantineTheme } from '@mantine/core';

import { toHighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

import { useBendCreatorFormContext } from '../Context/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from '../Context/useBendCreatorValidationContext';
import { BendAngleInput } from './BendAngleInput';
import { BendLengthStepper } from './BendLengthStepper';

/** Heights step by a quarter inch; every other length steps by an inch. In decimal feet. */
const QUARTER_INCH_STEP_KEYS: readonly BendInputKey[] = ['offsetHeight', 'kickHeight', 'saddleHeight'];
const QUARTER_INCH_IN_FEET = 0.25 / 12;
const ONE_INCH_IN_FEET = 1 / 12;

type Props = {
  bendTypeInput: BendTypeInput;
  isStockLengthField?: boolean;
};

export const BendDimensionInput = ({
  bendTypeInput: { name, keyName, isRequired, isAngle },
  isStockLengthField,
}: Props) => {
  const {
    setFieldValue,
    values: { maximizeLegLength: isUsingStockLength },
  } = useBendCreatorFormContext();
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();
  const labelObjectName = toHighlightableObjectName(name);
  const theme = useMantineTheme();

  const { fieldGetInputProps, validateBend } = useBendCreatorValidationContext();

  // TODO: Some of this probably more properly belongs in the validation context or form context rather than here.

  const { onFocus: formOnFocus, onBlur: formOnBlur, ...formInputProps } = fieldGetInputProps[keyName]();
  // Focusing a field highlights its dimension on the model too, since a touch screen has no hover.
  const fieldProps = {
    ...formInputProps,
    c: highlightedObject === labelObjectName ? theme.primaryColor : undefined,
    onMouseEnter: () => setHighlightedObject(labelObjectName),
    onMouseLeave: () => setHighlightedObject(null),
    onFocus: (event: FocusEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line typescript/no-unsafe-call
      formOnFocus?.(event);
      setHighlightedObject(labelObjectName);
    },
    onBlur: (event: FocusEvent<HTMLInputElement>) => {
      // oxlint-disable-next-line typescript/no-unsafe-call
      formOnBlur?.(event);
      setHighlightedObject(null);
    },
  };

  // Typed values validate on blur; a tapped value is complete, so it validates straight away.
  const setTappedValue = (value: number) => {
    setFieldValue(keyName, value);
    void validateBend(keyName, value);
  };

  return isAngle ? (
    <BendAngleInput
      label={name}
      required={isRequired}
      min={0.01}
      max={90}
      placeholder="Enter degrees"
      onQuickFill={setTappedValue}
      {...fieldProps}
    />
  ) : (
    <BendLengthStepper
      label={name}
      required={isRequired}
      allowBlank={!isRequired}
      step={QUARTER_INCH_STEP_KEYS.includes(keyName) ? QUARTER_INCH_IN_FEET : ONE_INCH_IN_FEET}
      onStep={setTappedValue}
      {...fieldProps}
      {...(isStockLengthField && isUsingStockLength
        ? {
            disabled: true,
            readOnly: true,
            description: 'This field will use the remaining stock length.',
          }
        : {})}
    />
  );
};
